import { initializePendo } from './initialize-pendo'
import { initializePaychexPendo } from './initialize-paychex-pendo'
import { injectPendo } from './inject-pendo'
import { injectPaychexPendo } from './inject-paychex-pendo'

const PENDO_TARGET_ROLES = ['account_admin', 'admin', 'author', 'it_admin']

const PENDO_TARGET_ACCOUNT_TYPES = ['paid', 'pilot']

export const PENDO_LOCAL_KEYNAME = 'pendo'
export const PENDO_LOCAL_INFO_DAYS_TO_LIVE = 30

export const isInPendoRole = (user) => {
  if (!user) return false

  const { roles } = user
  if (!roles) return false

  return roles.find((role) => PENDO_TARGET_ROLES.includes(role)) ? true : false
}

export const isPendoManager = (user) => {
  return !!user?.isManager
}

export const isTargetAccountType = (account) => {
  const accountType = account?.account?.accountType
  if (!accountType) return false

  return PENDO_TARGET_ACCOUNT_TYPES.includes(accountType.toLowerCase())
}

export const isBridgeSupportUser = (session) => {
  return !!session?.user?.isSupport
}

export const shouldInitPendo = (session) => {
  if (!session) return false

  if (isBridgeSupportUser(session)) return false

  // Eligible users are those in a target role or any manager (has direct reports).
  if (!isInPendoRole(session.user) && !isPendoManager(session.user))
    return false

  // NOTE: account-type filtering (isTargetAccountType) is intentionally not
  // applied for now. `accountType` proved unreliable — real, paying accounts
  // (e.g. Bridge's own internal account) carry a stale "test" label and were
  // being wrongly excluded. Whether to re-gate on account type, and on what
  // signal, is pending a decision with the Pendo owners (guide audience can be
  // scoped in Pendo via the tenantType metadata we already send). The
  // isTargetAccountType helper is retained so re-enabling is a one-line change.
  // if (!isTargetAccountType(account)) return false
  return true
}

export const isPaychexAccount = (account) => {
  return account?.tenant?.includes('paychex') ? true : false
}

export const isPaychexRootAccount = (account) => {
  if (!isPaychexAccount(account)) return false

  const subaccountSubdomain = account?.host?.subdomain
  const rootSubdomain = account?.account?.host?.subdomain

  return !!subaccountSubdomain && subaccountSubdomain === rootSubdomain
}

export const isPaychexSubaccount = (account) => {
  return isPaychexAccount(account) && !isPaychexRootAccount(account)
}

export const isPaychexPendoEnvironment = () => {
  return (
    process.env.DEPLOY_ENV === 'staging' ||
    process.env.DEPLOY_ENV === 'prod' ||
    process.env.DEPLOY_ENV === 'production'
  )
}

export function load() {
  return ({ account, session }) => {
    // Bridge support users are excluded from Pendo on every account.
    if (isBridgeSupportUser(session)) return

    // Paychex subaccounts get Paychex's own Pendo so Paychex admins can reach
    // their customers in the subs; the root falls through to our Pendo below.
    if (isPaychexPendoEnvironment() && isPaychexSubaccount(account)) {
      injectPaychexPendo()

      initializePaychexPendo(session, account)
    } else {
      if (process.env.PENDO_API_KEY) {
        injectPendo()

        if (!shouldInitPendo(session)) return

        initializePendo(session, account)
      }
    }
  }
}
